import type { HeldRole, PermissionScopeType } from '@repo/domain';

import type { PermissionTarget, RolePermissions } from '@features/auth/types';

interface ScopeNode {
  scopeType: PermissionScopeType;
  scopeId: string | undefined;
}

interface Attributes {
  heldRoles: HeldRole[] | undefined;
  permission: string;
  target: PermissionTarget | undefined;
  rolePermissions: RolePermissions;
}

const SYSTEM_NODE: ScopeNode = { scopeType: 'system', scopeId: undefined };

/** The nodes whose roles reach the target, from the target itself up to the whole system. */
const buildScopeChain = (target: PermissionTarget): ScopeNode[] => {
  if (target.system) {
    return [SYSTEM_NODE];
  }

  if (target.parkingId === undefined) {
    return [{ scopeType: 'organization', scopeId: target.organizationId }, SYSTEM_NODE];
  }

  const organizationNodes: ScopeNode[] =
    target.organizationId === undefined ? [] : [{ scopeType: 'organization', scopeId: target.organizationId }];

  return [{ scopeType: 'parking', scopeId: target.parkingId }, ...organizationNodes, SYSTEM_NODE];
};

/** A role counts only on the node type the contract lets it be granted on, as the backend reads it. */
const carriesPermission = (heldRole: HeldRole, permission: string, rolePermissions: RolePermissions): boolean => {
  const rolePermission = rolePermissions.get(heldRole.roleKey);

  return (
    rolePermission !== undefined &&
    rolePermission.scopeType === heldRole.scopeType &&
    rolePermission.permissionKeys.has(permission)
  );
};

const isHeldOn = (heldRole: HeldRole, node: ScopeNode): boolean =>
  heldRole.scopeType === node.scopeType && heldRole.scopeId === node.scopeId;

/**
 * Answers whether the held roles carry the permission on the target — or anywhere at all without a
 * target. Roles that are not known yet answer `false`.
 */
export function holdsPermission({ heldRoles, permission, target, rolePermissions }: Attributes): boolean {
  if (heldRoles === undefined) return false;

  const grantingRoles = heldRoles.filter((heldRole) => carriesPermission(heldRole, permission, rolePermissions));

  if (target === undefined) return grantingRoles.length > 0;

  const scopeChain = buildScopeChain(target);

  return grantingRoles.some((heldRole) => scopeChain.some((node) => isHeldOn(heldRole, node)));
}
